// Convert experiment tracking. Replaces the previous Optimizely integration (see git history /
// src/utils/optimizely.js for the old implementation).
//
// Ported from `Track.Experiment` in universal-ovm-fe's src/utils/track/track.ts, trimmed to plain
// JS (this app doesn't use TypeScript) and to a single Convert project/page, since old-ovm-fe only
// ever runs as the "bussgeldcheck" experience (see the Convert script tag in public/index.html).

const CONVERT_VISITOR_ID_MAX_LENGTH = 256

// Convert event -> conversion id, scoped to the "bussgeldcheck" project. Keep this in sync with
// the `bussgeldcheck` entries of MAP_EVENT_TO_CONVERT_EVENT in universal-ovm-fe's track.ts.
//
// Note: `personalInfoSent` (100157177) is intentionally omitted here. The "info" intake section it
// tracks doesn't exist in this (old) version of the attachment flow and is not planned to be
// backported, so there's nothing to attach that event to.
const MAP_EVENT_TO_CONVERT_EVENT = {
  clickFirstWeiter: '100153813',
  poaModalClick: '100153814',
  termsModalClick: '100153815',
  manualSignatureClick: '100153816',
  scrolledToSignCTA: '100153817',
  paymentOpened: '100153820',
  paymentSuccess: '100153823',
  paymentFailed: '100153824',
  paymentInitiation: '100153821',
  oneTimePaymentInitiation: '100153822',
  ovmSignSuccess: '100153818',
  ovmSignFailure: '100153819',
  insuranceSubmitted: '100157175',
  fileUpdated: '100157176',
  additionalInfoSent: '100157178',
}

// Page names Convert expects a manual 'run' trigger for.
const MAP_PAGE_TO_CONVERT_PAGE = ['moneyBackGuarantee', 'attachment']

const log = (...args) => {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.log(...args)
  }
}

class ExperimentTracker {
  lastConvertVisitorId = null

  ToConvertEvent(convertId) {
    window._conv_q = window._conv_q || []
    window._conv_q.push(['triggerConversion', convertId])
  }

  ToConvertPage(page) {
    // Set the flag before pushing 'run': if Convert's script is already loaded and processes
    // the queue synchronously (rather than draining it on its own tick), a JS-condition Location
    // checking `window[page] === true` could evaluate before the flag is set if pushed first.
    window[page] = true
    window._conv_q = window._conv_q || []
    window._conv_q.push(['run', 'true'])
  }

  ToConvertVisitorId(visitorId) {
    window._conv_q = window._conv_q || []
    window._conv_q.push({ what: 'identify', params: { visitorId } })
  }

  ToExperiments(data) {
    if (data.type === 'event') {
      const convertId = MAP_EVENT_TO_CONVERT_EVENT[data.eventName]
      if (!convertId) return
      this.ToConvertEvent(convertId)
      return
    }

    if (data.type === 'page') {
      if (!MAP_PAGE_TO_CONVERT_PAGE.includes(data.pageName)) return
      this.ToConvertPage(data.pageName)
      return
    }
  }

  Event(eventName) {
    log(`📊 - tracking [Event] event: ${eventName} (type: event)`)
    this.ToExperiments({ type: 'event', eventName })
  }

  Page(pageName) {
    log(`📊 - tracking [Page] event: ${pageName}`)
    this.ToExperiments({ type: 'page', pageName })
  }

  Identify(visitorId) {
    if (typeof visitorId !== 'string') return
    const trimmed = visitorId.trim()
    if (trimmed.length === 0 || trimmed.length > CONVERT_VISITOR_ID_MAX_LENGTH) return
    if (this.lastConvertVisitorId === trimmed) return
    try {
      this.ToConvertVisitorId(trimmed)
      this.lastConvertVisitorId = trimmed
      log(`🪪 - tracking [Identify] convert visitor id: ${trimmed}`)
    } catch (error) {
      log('🚫 - tracking [Identify] failed to push identify', error)
    }
  }

  GetActiveExperiments() {
    try {
      const experiences = (window.convert && window.convert.currentData && window.convert.currentData.experiences) || {}
      return Object.entries(experiences).reduce((acc, [experimentId, experience]) => {
        const variationId = experience && experience.variation && experience.variation.id
        if (variationId) acc.push({ campaignId: '', experimentId, variationId: String(variationId) })
        return acc
      }, [])
    } catch (error) {
      log('🚫 - tracking [GetActiveExperiments] failed to read active experiments', error)
      return []
    }
  }

  OnReady(callback) {
    window._conv_q = window._conv_q || []
    window._conv_q.push([callback])
  }
}

export class Track {
  static Experiment = new ExperimentTracker()
}

// Runs `run` only when `condition` is true, otherwise skips it. Use this to guard tracking that
// must fire once per case (e.g. only on the first successful submission of a section), so
// resubmissions (a section reopened and resent after a refresh) don't double-count.
export const trackOnce = (condition, run) => {
  if (!condition) return
  run()
}
